/**
 * Der Preisplan aus der KI — geprüft, bevor ihn jemand zu sehen bekommt.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * WARUM ES DIESE DATEI GIBT.
 *
 * Ein Absatz Fließtext über Preisgestaltung hilft niemandem. Was hilft, ist eine
 * Zeile, die man ankreuzt und die danach als echte Saison in der Preisliste steht.
 * Also lässt sich die KI strukturiert antworten — und alles, was strukturiert
 * hereinkommt, muss geprüft werden, BEVOR es auf den Schirm kommt.
 *
 * WARUM SO HART GEPRÜFT WIRD. Ein falscher Preis, den jemand übernimmt, kostet
 * echtes Geld — und zwar für jede Nacht des Zeitraums, bis es auffällt. Ein
 * Sprachmodell, das sich um eine Null vertut, ist kein exotischer Fall: 1.290 €
 * statt 129 € ist ein Zeichen Unterschied. Deshalb gilt hier durchgehend die
 * strengere Lesart — im Zweifel verwerfen. Ein Vorschlag, der fehlt, ärgert; ein
 * Vorschlag, der falsch ist, kostet.
 *
 * WARUM DIE PRÜFUNG NICHT NUR „PLAUSIBEL" MELDET, SONDERN AUCH WARUM. Der
 * Vermieter soll sehen, dass geprüft wurde. „Zwei Vorschläge verworfen, weil sich
 * die Zeiträume überschnitten" ist ein Vertrauensbeweis; stilles Wegwerfen sieht
 * aus wie eine KI, die eben mal drei und mal fünf Zeilen liefert.
 *
 * WAS HIER NICHT PASSIERT: gerechnet wird nichts Neues. Der heutige Preis eines
 * Zeitraums kommt aus `resolveNightlyRate`, die Priorität aus `proposedPriority`,
 * die Wochenendmaske aus `priceAdvice.ts`. Eine zweite Preislogik neben der
 * bestehenden wäre eine zweite Quelle für dieselbe Zahl — und irgendwann zwei
 * verschiedene Antworten auf dieselbe Frage.
 *
 * Reine Funktionen, kein React, kein Netz — getestet in pricePlan.test.ts.
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import type { ISODate, RatePeriod, Unit } from '../data/model'
import { addDays, addMonths, nights } from './dates'
import { resolveNightlyRate } from './pricing'
import { ALL_DAYS_MASK, WEEKEND_DOW_MASK, WEEKEND_RULE_MONTHS, proposedPriority } from './priceAdvice'

/* ────────────────────────────────────────────────────────── Die Grenzwerte
 *
 * Alle Bänder stehen hier oben, damit man über sie streiten kann, ohne die Prüfung
 * zu lesen. Jedes hat einen Grund, und der steht dabei.
 */

/**
 * Absolute Unter- und Obergrenze für einen Nachtpreis, in der Währung des Betriebs.
 *
 * StaySuite verkauft an kleine Vermieter mit Zimmern und Ferienwohnungen — 15 € ist
 * unter jedem Selbstkostenpreis, 1.500 € über allem, was in diesem Kundenkreis
 * vorkommt. Beides ist mit hoher Wahrscheinlichkeit ein verrutschtes Komma und mit
 * geringer Wahrscheinlichkeit ein echter Vorschlag. Diese Abwägung geht zulasten
 * des seltenen Luxuschalets: es bekommt lieber keinen Vorschlag als das
 * Zimmervermieter-Massenpublikum einen zehnfach falschen.
 */
export const MIN_NIGHTLY_PRICE = 15
export const MAX_NIGHTLY_PRICE = 1500

/**
 * Wie weit ein Saisonpreis vom Grundpreis desselben Plans abweichen darf.
 *
 * Eine Nebensaison unter der Hälfte des Grundpreises ist kein Saisonpreis mehr,
 * sondern ein Räumungsverkauf; das Zweieinhalbfache erreicht in diesem Kundenkreis
 * nicht einmal Silvester in einem Skiort. Der Faktor prüft die INNERE Stimmigkeit
 * des Plans — eine Zeile, die zum eigenen Grundpreis nicht passt, ist ein
 * Ausrutscher des Modells und kein mutiger Vorschlag.
 */
export const MIN_SEASON_FACTOR = 0.5
export const MAX_SEASON_FACTOR = 2.5

/**
 * Wie viele Saisonstufen ein Plan höchstens haben darf.
 *
 * Sechs sind bereits mehr, als ein kleiner Betrieb im Kopf behält (Vor-, Haupt-,
 * Nebensaison, Weihnachten, Ostern, Ferien). Wer zwölf Stufen bekommt, pflegt sie
 * nach einem Jahr nicht mehr und misstraut dann der ganzen Preisliste.
 */
export const MAX_PLAN_PERIODS = 6

/**
 * Kürzeste und längste Laufzeit eines Saisonzeitraums, in Tagen.
 *
 * Unter zwei Tagen ist es kein Zeitraum, sondern ein Feiertag — dafür gibt es
 * Zuschläge. Über 200 Tage ist es keine Saison mehr, sondern der Normalpreis unter
 * falschem Namen; er würde den Grundpreis stillschweigend überschreiben.
 */
export const MIN_PERIOD_DAYS = 2
export const MAX_PERIOD_DAYS = 200

/**
 * Wie weit ein Zeitraum in der Zukunft beginnen darf.
 *
 * Achtzehn Monate: ein Vorschlag für übernächsten Sommer ist nicht falsch, aber er
 * ist nicht entscheidbar — bis dahin ändern sich Kosten, Haus und Nachfrage.
 */
export const MAX_LEAD_MONTHS = 18

/** Mindest- und Höchstwert für einen Mindestaufenthalt. */
export const MIN_MIN_NIGHTS = 1
export const MAX_MIN_NIGHTS = 21

/** Obergrenze für den Wochenendaufschlag in Prozent. Darüber ist es keine Nuance mehr. */
export const MAX_WEEKEND_PCT = 40

/** Länger liest die Begründung niemand — und ein Modell, das ausufert, hat nichts zu sagen. */
const MAX_REASON_CHARS = 400

/**
 * Ab dieser Laufzeit ist eine hinterlegte Zeile der Grundpreis und keine Saison.
 *
 * Bewusst über `MAX_PERIOD_DAYS`: sonst könnte eine gerade erst angelegte, lange
 * Saison beim nächsten Durchlauf als Grundpreis durchgehen und überschrieben werden.
 */
const BASE_RATE_MIN_DAYS = 300

/** Wie lange ein neu angelegter Grundpreis gilt, wenn es noch keinen gibt. */
const BASE_RATE_MONTHS = 24

/* ──────────────────────────────────────────────────────────────────── Typen */

/** Eine geprüfte Saisonzeile des Plans. */
export interface PlanPeriod {
  /** Stabil über die Zeilen des Plans (`p1`, `p2`, …) — Schlüssel für Auswahl und Liste. */
  id: string
  name: string
  from: ISODate
  /** Letzter Tag, EINSCHLIESSLICH — so, wie `rate_periods.valid_to` gelesen wird. */
  lastDay: ISODate
  pricePerNight: number
  minNights: number | null
  /** Warum dieser Preis für diesen Zeitraum. Ohne sie kommt die Zeile nicht durch. */
  reason: string
}

export interface PricePlan {
  /** Der Preis, der gilt, wenn keine Saison greift. */
  basePrice: number
  baseReason: string
  /** Mindestaufenthalt außerhalb der Saisons. Null = keine Vorgabe. */
  baseMinNights: number | null
  periods: PlanPeriod[]
  /** Aufschlag auf Fr- und Sa-Nächte, in Prozent. Null = kein Vorschlag. */
  weekendPct: number | null
  weekendReason: string
  /**
   * Worauf der Plan beruht, in einem Satz vom Modell selbst. Steht auf dem Schirm
   * neben den Zahlen — ein Vorschlag ohne Angabe seiner Grundlage ist eine
   * Behauptung.
   */
  basis: string
}

export type PlanIssueKind =
  /** Die Antwort war kein verwertbares JSON. Dann gibt es gar keinen Plan. */
  | 'not_json'
  /** JSON, aber ohne brauchbaren Grundpreis — alles andere hängt daran. */
  | 'no_base_price'
  /** Datum unlesbar, verdreht oder kein Kalendertag. */
  | 'bad_dates'
  /** Der Zeitraum liegt vollständig hinter uns. */
  | 'past'
  /** Zu kurz oder zu lang für eine Saison. */
  | 'bad_length'
  /** Beginnt so weit in der Zukunft, dass er nicht entscheidbar ist. */
  | 'too_far'
  /** Preis außerhalb der absoluten Grenzen oder unstimmig zum Grundpreis. */
  | 'price_out_of_range'
  /** Überschneidet einen bereits angenommenen Zeitraum. */
  | 'overlap'
  /** Mehr Saisonstufen, als ein Betrieb pflegen kann. */
  | 'too_many'
  /** Keine Begründung — und ohne Begründung keine Zahl. */
  | 'no_reason'
  /** Der Zeitraum begann in der Vergangenheit und wurde auf heute gezogen. */
  | 'clamped_to_today'
  /** Unsinniger Mindestaufenthalt; die Zeile bleibt, das Feld fällt weg. */
  | 'min_nights_dropped'
  /** Unsinniger Wochenendaufschlag; der Vorschlag entfällt. */
  | 'weekend_dropped'

export interface PlanIssue {
  kind: PlanIssueKind
  /**
   * Woran der Leser die betroffene Zeile erkennt — der Name aus dem Vorschlag oder
   * sein Zeitraum. Nie ein Fehlercode, nie eine technische Kennung.
   */
  label: string
}

export interface PricePlanCheck {
  /** Null, wenn nichts Verwertbares übrig blieb. */
  plan: PricePlan | null
  /** Was verworfen oder zurechtgestutzt wurde. Gehört sichtbar unter den Plan. */
  issues: PlanIssue[]
}

export interface PlanCheckContext {
  today: ISODate
  /**
   * Der heutige Grundpreis des Betriebs. Dient als Rückfallwert, wenn das Modell
   * keinen nennt — NICHT als Band, gegen das geprüft wird: beim Kaltstart ist der
   * hinterlegte Preis der Platzhalter aus der Einrichtung und keine Entscheidung.
   */
  referencePrice?: number | null
}

/* ─────────────────────────────────────────────────────────── JSON herauslösen */

/**
 * Das JSON-Objekt aus der Antwort des Modells.
 *
 * Toleriert Code-Zäune und einen Satz davor oder dahinter, weil beides vorkommt,
 * obwohl der Systemprompt es untersagt — und ein an sich brauchbarer Plan an einem
 * ```json davor zu scheitern wäre eine bezahlte Antwort im Müll. Toleriert
 * ausdrücklich NICHT halbwegs passende Zahlen: was nicht als Objekt dasteht, ist
 * weg.
 */
export function parsePlanJson(text: string): unknown {
  const trimmed = text.trim()
  const candidates: string[] = []

  const fenced = /```(?:json)?\s*([\s\S]*?)```/.exec(trimmed)
  if (fenced?.[1]) candidates.push(fenced[1].trim())
  candidates.push(trimmed)

  const first = trimmed.indexOf('{')
  const last = trimmed.lastIndexOf('}')
  if (first >= 0 && last > first) candidates.push(trimmed.slice(first, last + 1))

  for (const c of candidates) {
    try {
      const parsed: unknown = JSON.parse(c)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch {
      // Nächster Kandidat. Ein Parserfehler ist hier kein Fehler, sondern ein Nein.
    }
  }
  return null
}

/* ─────────────────────────────────────────────────────────── Kleinkram, geprüft */

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null
}

/** Eine endliche Zahl — auch aus `"129"`, weil Modelle Zahlen gern in Anführungszeichen setzen. */
function numberOf(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.replace(',', '.'))
    return Number.isFinite(n) ? n : null
  }
  return null
}

function textOf(v: unknown): string {
  return typeof v === 'string' ? v.trim().slice(0, MAX_REASON_CHARS) : ''
}

/**
 * Ein echter Kalendertag im Format `YYYY-MM-DD`.
 *
 * Der Rückweg über `Date` ist der Punkt: „2026-02-30" ist formal korrekt und
 * existiert trotzdem nicht — `Date.UTC` rollt ihn auf den 2. März weiter, und genau
 * daran erkennt man ihn. Bewusst mit `Date.UTC` und nicht mit `fromISO`, weil hier
 * nur die Existenz des Tages geprüft wird und keine Zeitzone im Spiel sein darf.
 */
export function isCalendarDate(v: unknown): v is ISODate {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false
  const [y, m, d] = v.split('-').map(Number) as [number, number, number]
  if (m < 1 || m > 12 || d < 1 || d > 31) return false
  const probe = new Date(Date.UTC(y, m - 1, d))
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d
}

/** Halboffene Überschneidung zweier einschließender Zeiträume. */
function overlapsInclusive(
  aFrom: ISODate, aLast: ISODate, bFrom: ISODate, bLast: ISODate,
): boolean {
  return aFrom <= bLast && bFrom <= aLast
}

/* ──────────────────────────────────────────────────────────────── Die Prüfung */

/**
 * Aus der rohen Antwort ein Plan, dem man Geld anvertrauen kann — oder nichts.
 *
 * Die Reihenfolge der Prüfungen ist Absicht: erst der Grundpreis (ohne ihn hat
 * keine Saisonzeile einen Maßstab), dann die Zeilen einzeln, dann die Zeilen
 * gegeneinander (Überschneidung), zuletzt die Menge. So trifft jede Zeile die
 * Ablehnung, die sie verdient, und nicht die des Nachbarn.
 */
export function validatePricePlan(raw: unknown, ctx: PlanCheckContext): PricePlanCheck {
  const issues: PlanIssue[] = []
  const root = obj(raw)
  if (!root) return { plan: null, issues: [{ kind: 'not_json', label: '' }] }

  /* ---- Grundpreis ---- */
  const rawBase = numberOf(root.basePrice) ?? ctx.referencePrice ?? null
  const basePrice = rawBase != null ? Math.round(rawBase) : null
  if (basePrice == null || basePrice < MIN_NIGHTLY_PRICE || basePrice > MAX_NIGHTLY_PRICE) {
    return { plan: null, issues: [{ kind: 'no_base_price', label: '' }] }
  }

  const baseReason = textOf(root.baseReason)
  if (!baseReason) return { plan: null, issues: [{ kind: 'no_reason', label: '' }] }

  /* ---- Mindestaufenthalt außerhalb der Saison ---- */
  let baseMinNights = plausibleMinNights(root.baseMinNights ?? root.minNights)
  if (baseMinNights === 'dropped') {
    issues.push({ kind: 'min_nights_dropped', label: baseReason })
    baseMinNights = null
  }

  /* ---- Wochenendaufschlag ---- */
  const rawPct = numberOf(root.weekendPct ?? root.weekendSurchargePct)
  const weekendReason = textOf(root.weekendReason)
  let weekendPct: number | null = null
  if (rawPct != null) {
    const pct = Math.round(rawPct)
    if (pct > 0 && pct <= MAX_WEEKEND_PCT && weekendReason) weekendPct = pct
    else issues.push({ kind: 'weekend_dropped', label: weekendReason })
  }

  /* ---- Die Zeiträume ---- */
  const horizon = addMonths(ctx.today, MAX_LEAD_MONTHS)
  const rawPeriods = Array.isArray(root.periods) ? root.periods : []
  const accepted: PlanPeriod[] = []

  // Chronologisch geprüft, damit bei einer Überschneidung immer die SPÄTERE Zeile
  // fällt. Ohne feste Reihenfolge hinge es an der Laune des Modells, welcher der
  // beiden Preise gilt — und der Vermieter sähe nie, dass es eine Wahl gab.
  const sorted = rawPeriods
    .map((p) => obj(p))
    .filter((p): p is Record<string, unknown> => p != null)
    .sort((a, b) => String(a.from ?? '').localeCompare(String(b.from ?? '')))

  for (const p of sorted) {
    const name = textOf(p.name)
    const from = p.from
    const lastRaw = p.to ?? p.lastDay
    const label = name || (isCalendarDate(from) ? String(from) : '?')

    if (!isCalendarDate(from) || !isCalendarDate(lastRaw) || lastRaw < from) {
      issues.push({ kind: 'bad_dates', label })
      continue
    }

    if (lastRaw < ctx.today) { issues.push({ kind: 'past', label }); continue }
    if (from > horizon) { issues.push({ kind: 'too_far', label }); continue }

    // Ein bereits laufender Zeitraum wird auf heute gezogen statt verworfen: am
    // 15. Juli eine „Sommersaison 1. Juli bis 31. August" wegzuwerfen, wäre für den
    // Vermieter unverständlich — die restlichen sechs Wochen sind ja noch zu
    // bepreisen. Vergangene Nächte ändert das nicht: bestehende Buchungen tragen
    // ihren Preis bereits bei sich.
    let start: ISODate = from
    if (start < ctx.today) {
      start = ctx.today
      issues.push({ kind: 'clamped_to_today', label })
    }

    const days = nights(start, lastRaw) + 1
    if (days < MIN_PERIOD_DAYS || days > MAX_PERIOD_DAYS) {
      issues.push({ kind: 'bad_length', label })
      continue
    }

    const price = numberOf(p.pricePerNight ?? p.price)
    const rounded = price != null ? Math.round(price) : null
    if (
      rounded == null ||
      rounded < MIN_NIGHTLY_PRICE || rounded > MAX_NIGHTLY_PRICE ||
      rounded < basePrice * MIN_SEASON_FACTOR || rounded > basePrice * MAX_SEASON_FACTOR
    ) {
      issues.push({ kind: 'price_out_of_range', label })
      continue
    }

    const reason = textOf(p.reason)
    if (!reason) { issues.push({ kind: 'no_reason', label }); continue }

    if (accepted.some((a) => overlapsInclusive(a.from, a.lastDay, start, lastRaw))) {
      issues.push({ kind: 'overlap', label })
      continue
    }

    if (accepted.length >= MAX_PLAN_PERIODS) {
      issues.push({ kind: 'too_many', label })
      continue
    }

    let minNights = plausibleMinNights(p.minNights)
    if (minNights === 'dropped') {
      issues.push({ kind: 'min_nights_dropped', label })
      minNights = null
    }

    accepted.push({
      id: `p${accepted.length + 1}`,
      name: name || defaultPeriodName(start, lastRaw),
      from: start,
      lastDay: lastRaw,
      pricePerNight: rounded,
      minNights,
      reason,
    })
  }

  return {
    plan: {
      basePrice,
      baseReason,
      baseMinNights,
      periods: accepted,
      weekendPct,
      weekendReason: weekendPct != null ? weekendReason : '',
      basis: textOf(root.basis),
    },
    issues,
  }
}

/** `null` = nichts angegeben, `'dropped'` = angegeben und unbrauchbar. */
function plausibleMinNights(v: unknown): number | null | 'dropped' {
  if (v == null || v === '') return null
  const n = numberOf(v)
  if (n == null) return 'dropped'
  const r = Math.round(n)
  if (r < MIN_MIN_NIGHTS || r > MAX_MIN_NIGHTS) return 'dropped'
  return r
}

/** „01.07.–31.08." als Rückfall, wenn das Modell keinen Namen mitgeschickt hat. */
export function defaultPeriodName(from: ISODate, lastDay: ISODate): string {
  const dm = (iso: ISODate) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`
  return `${dm(from)}–${dm(lastDay)}`
}

/* ──────────────────────────────────────────── Was der Zeitraum heute kostet */

/**
 * Der mittlere Nachtpreis, den der Betrieb HEUTE für diesen Zeitraum verlangt.
 *
 * Die Zahl steht neben jedem Vorschlag, weil ein Preis ohne Vergleich keine
 * Entscheidung zulässt: „129 €" sagt nichts, „129 € statt heute 95 €" sagt alles.
 * Sie kommt aus `resolveNightlyRate` und damit aus derselben Rechnung, die auch die
 * Buchung bepreist — eine eigene Näherung hier würde früher oder später von der
 * echten abweichen.
 */
export function currentAveragePrice(
  units: Unit[], ratePeriods: RatePeriod[], from: ISODate, lastDay: ISODate,
): number {
  const active = units.filter((u) => u.active)
  if (active.length === 0 || lastDay < from) return 0

  let sum = 0
  let count = 0
  for (let d = from; d <= lastDay; d = addDays(d, 1)) {
    for (const u of active) {
      sum += resolveNightlyRate(u, ratePeriods, d).price
      count += 1
    }
  }
  return count > 0 ? sum / count : 0
}

/* ─────────────────────────────────────────── Aus dem Plan werden Preiszeilen */

/**
 * Die hausweite Zeile, die heute als Grundpreis wirkt.
 *
 * Gesucht wird die LÄNGSTE, hausweite, an allen Tagen geltende Zeile, die heute
 * greift — das ist die „Standard"-Zeile, die die Einrichtung anlegt. Wird sie
 * gefunden, ändert der Plan ihren Preis, statt eine zweite Ganzjahreszeile
 * danebenzustellen: zwei überlappende Ganzjahrespreise mit unterschiedlichen
 * Prioritäten sind der sicherste Weg, den eigenen Preis nicht mehr zu verstehen.
 *
 * Bei mehreren gleich langen gewinnt die mit der niedrigsten Priorität: das ist die
 * unterste Ebene der Preisliste, und genau die ist der Grundpreis.
 */
export function findBaseRate(periods: RatePeriod[], today: ISODate): RatePeriod | null {
  const candidates = periods.filter(
    (p) =>
      p.active &&
      p.unit_id == null &&
      (p.category_id ?? null) == null &&
      p.dow_mask === ALL_DAYS_MASK &&
      p.valid_from <= today &&
      p.valid_to >= today &&
      nights(p.valid_from, p.valid_to) + 1 >= BASE_RATE_MIN_DAYS,
  )
  if (candidates.length === 0) return null

  return [...candidates].sort((a, b) => {
    const lenA = nights(a.valid_from, a.valid_to)
    const lenB = nights(b.valid_from, b.valid_to)
    return lenB - lenA || a.priority - b.priority || a.id.localeCompare(b.id)
  })[0]!
}

/**
 * Ob die Preisliste über den Grundpreis hinaus überhaupt etwas enthält, das noch gilt.
 *
 * Das ist die Frage „steht dieser Vermieter vor einer leeren Preisliste?" — und sie
 * lautet bewusst nicht „gibt es Zeilen?". Die Einrichtung legt eine einzige
 * Ganzjahreszeile an (`create_my_tenant`, Migration 0005), damit der Kalender von
 * der ersten Minute an etwas bepreist. Wer nur diese hat, hat noch keine Preise
 * gemacht — er hat einen Platzhalter.
 */
export function hasSeasonalRates(periods: RatePeriod[], today: ISODate): boolean {
  const base = findBaseRate(periods, today)
  return periods.some((p) => p.active && p.id !== base?.id && p.valid_to >= today)
}

export interface PlanApplyInput {
  plan: PricePlan
  /** Welche Zeilen übernommen werden: `base`, `weekend` und die Zeitraum-Ids. */
  selected: ReadonlySet<string>
  /** Vom Vermieter geänderte Preise, nach Zeilen-Id. Leer = Vorschlag unverändert. */
  priceOverrides?: ReadonlyMap<string, number>
  tenantId: string
  today: ISODate
  /** Der aktuelle Stand der Preisliste. Wird nicht verändert. */
  existing: RatePeriod[]
  /** Woher die Ids kommen. Ausgelagert, damit der Test feste Ids setzen kann. */
  newId: () => string
  /**
   * Bezeichnungen für die beiden Zeilen, die nicht vom Modell benannt werden.
   * Von außen, weil sie in der Sprache des Vermieters in seiner Preisliste stehen —
   * und diese Datei nichts von der Oberfläche weiß.
   */
  labels: { base: string; weekend: string }
}

/** Die Kennung der Grundpreiszeile in `selected`. */
export const BASE_ROW_ID = 'base'
/** Die Kennung der Wochenendzeile in `selected`. */
export const WEEKEND_ROW_ID = 'weekend'

/**
 * Der Plan als Zeilen für `upsertRatePeriod` — in der Reihenfolge, in der sie
 * geschrieben werden müssen.
 *
 * WARUM DIE REIHENFOLGE ZÄHLT: `proposedPriority` liest den aktuellen Stand der
 * Preisliste. Würde man alle Zeilen gegen den ALTEN Stand berechnen, bekäme der
 * Grundpreis und die erste Saison dieselbe Priorität — bei gleicher Reichweite
 * entscheidet dann nichts mehr, und welcher Preis gilt, wäre Zufall. Deshalb wächst
 * die Liste mit: erst der Grundpreis, dann die Saisons, zuletzt der Zuschlag, der
 * sich über alles legen soll.
 *
 * Der Grundpreis ÜBERSCHREIBT die vorhandene Ganzjahreszeile, falls es eine gibt
 * (siehe `findBaseRate`) — dieselbe Id, also ein Update und keine zweite Zeile.
 */
export function planToRatePeriods(input: PlanApplyInput): RatePeriod[] {
  const { plan, selected, tenantId, today, newId } = input
  const price = (id: string, fallback: number) => {
    const over = input.priceOverrides?.get(id)
    return over != null && Number.isFinite(over) && over > 0 ? over : fallback
  }

  const out: RatePeriod[] = []
  const acc = [...input.existing]

  const row = (over: Partial<RatePeriod> & Pick<RatePeriod,
    'id' | 'name' | 'valid_from' | 'valid_to' | 'dow_mask' | 'price_per_night' | 'priority'>,
  ): RatePeriod => ({
    tenant_id: tenantId,
    unit_id: null,
    category_id: null,
    extra_person_price: null,
    base_occupancy: null,
    min_nights: null,
    active: true,
    online_enquiry: true,
    ...over,
  })

  if (selected.has(BASE_ROW_ID)) {
    const existingBase = findBaseRate(input.existing, today)
    const basePrice = price(BASE_ROW_ID, plan.basePrice)
    const baseRow = existingBase
      ? {
          ...existingBase,
          price_per_night: basePrice,
          min_nights: plan.baseMinNights ?? existingBase.min_nights,
        }
      : row({
          id: newId(),
          name: input.labels.base,
          valid_from: today,
          valid_to: addDays(addMonths(today, BASE_RATE_MONTHS), -1),
          dow_mask: ALL_DAYS_MASK,
          price_per_night: basePrice,
          min_nights: plan.baseMinNights,
          priority: 0,
        })
    out.push(baseRow)
    const at = acc.findIndex((r) => r.id === baseRow.id)
    if (at >= 0) acc[at] = baseRow
    else acc.push(baseRow)
  }

  for (const p of plan.periods) {
    if (!selected.has(p.id)) continue
    const season = row({
      id: newId(),
      name: p.name,
      valid_from: p.from,
      valid_to: p.lastDay,
      dow_mask: ALL_DAYS_MASK,
      price_per_night: price(p.id, p.pricePerNight),
      min_nights: p.minNights,
      priority: proposedPriority(acc, p.from, p.lastDay, ALL_DAYS_MASK),
    })
    out.push(season)
    acc.push(season)
  }

  if (selected.has(WEEKEND_ROW_ID) && plan.weekendPct != null) {
    const from = today
    const lastDay = addDays(addMonths(today, WEEKEND_RULE_MONTHS), -1)
    // Der Zuschlag rechnet auf den GRUNDPREIS und nicht auf die jeweilige Saison:
    // eine Preiszeile trägt einen absoluten Betrag, keinen Prozentsatz. Auf dem
    // Schirm steht deshalb der Euro-Betrag, den der Vermieter überschreiben kann.
    const weekendRow = row({
      id: newId(),
      name: input.labels.weekend,
      valid_from: from,
      valid_to: lastDay,
      dow_mask: WEEKEND_DOW_MASK,
      // Ganzzahlig gerechnet (× (100 + pct) / 100 statt × (1 + pct/100)): sonst
      // macht die Fließkommadarstellung aus 110 € plus 15 % die 126,4999…, und der
      // Zuschlag landet einen Euro zu niedrig in der Preisliste.
      price_per_night: price(
        WEEKEND_ROW_ID,
        Math.round(price(BASE_ROW_ID, plan.basePrice) * (100 + plan.weekendPct) / 100),
      ),
      priority: proposedPriority(acc, from, lastDay, WEEKEND_DOW_MASK),
    })
    out.push(weekendRow)
    acc.push(weekendRow)
  }

  return out
}

/* ═══════════════════════════════════════════════════ Was die KI zu hören bekommt
 *
 * Die Prompts stehen bei der Prüfung und nicht bei der Oberfläche, weil sie
 * dieselbe Frage von der anderen Seite beantworten: oben steht, was hereinkommen
 * DARF, hier steht, was hereinkommen SOLL. Driften die beiden auseinander, wirft
 * die Prüfung reihenweise brauchbare Zeilen weg und niemand versteht, warum.
 */

/** Das Antwortschema, wörtlich so im Prompt — einmal geschrieben, zweimal benutzt. */
const SCHEMA = `{
  "basePrice": 95,
  "baseReason": "...",
  "baseMinNights": 2,
  "weekendPct": 12,
  "weekendReason": "...",
  "periods": [
    { "name": "...", "from": "YYYY-MM-DD", "to": "YYYY-MM-DD",
      "pricePerNight": 130, "minNights": 5, "reason": "..." }
  ],
  "basis": "..."
}`

/**
 * Die Hausordnung für das Modell.
 *
 * DER WICHTIGSTE SATZ IST DAS VERBOT ERFUNDENER VERGLEICHSZAHLEN. Ein Sprachmodell
 * hat keinen Zugriff auf die Preise der Nachbarhäuser, formuliert aber bereitwillig
 * „12 % unter vergleichbaren Objekten im Ort" — eine Zahl, die überzeugend klingt,
 * frei erfunden ist und beim ersten Gegenbeispiel das Vertrauen in jede andere Zahl
 * des Produkts mitreißt. Das Verbot steht deshalb nicht als Fußnote, sondern als
 * erste Regel, und die Oberfläche schreibt zusätzlich neben das Ergebnis, worauf es
 * beruht.
 *
 * Die Zahlenbänder sind dieselben wie in der Prüfung oben — ein Modell, das sie
 * kennt, liefert weniger Zeilen, die verworfen werden müssen.
 */
export function pricePlanSystem(de: boolean, currency: string): string {
  return de
    ? [
        'Du berätst kleine Vermieter im deutschsprachigen Raum bei ihrer Preisliste.',
        '',
        'ERFINDE KEINE MARKTDATEN. Du hast keinen Zugriff auf die Preise anderer',
        'Häuser, auf Buchungsportale oder auf Statistiken. Nenne NIEMALS eine',
        'konkrete Vergleichszahl („vergleichbare Häuser verlangen 140 €", „12 % unter',
        'dem Ortsdurchschnitt", „die Auslastung im Ort liegt bei 70 %"). Solche Zahlen',
        'wären erfunden. Deine Grundlage ist allgemeines Wissen über die Region, die',
        'Jahreszeiten und die Art des Objekts — sag das in "basis" in einem Satz und',
        'nenne den Vorschlag dort ausdrücklich einen Anhaltspunkt und keinen Marktpreis.',
        '',
        'Antworte AUSSCHLIESSLICH mit diesem JSON-Objekt, ohne Text davor oder danach',
        'und ohne Code-Zaun:',
        SCHEMA,
        '',
        'Regeln:',
        `- Alle Preise sind ganze Beträge in ${currency}, pro Nacht und für die ganze`,
        '  Einheit, so wie sie der Gast sieht (inklusive der üblichen Steuer).',
        `- "to" ist der letzte Tag EINSCHLIESSLICH.`,
        `- Höchstens ${MAX_PLAN_PERIODS} Zeiträume. Sie dürfen einander NICHT`,
        `  überschneiden, müssen mindestens ${MIN_PERIOD_DAYS} und höchstens`,
        `  ${MAX_PERIOD_DAYS} Tage lang sein und dürfen nicht in der Vergangenheit liegen.`,
        `- Saisonpreise liegen zwischen dem ${MIN_SEASON_FACTOR}- und dem`,
        `  ${MAX_SEASON_FACTOR}-Fachen von "basePrice".`,
        `- "minNights" zwischen ${MIN_MIN_NIGHTS} und ${MAX_MIN_NIGHTS}, oder weglassen.`,
        `- "weekendPct" höchstens ${MAX_WEEKEND_PCT}; weglassen, wenn kein Aufschlag passt.`,
        '- Zu JEDER Zahl gehört ein "reason": ein Satz in der Sprache eines Vermieters,',
        '  der sagt, WARUM — Jahreszeit, Ferien, Anlass, Nachfragemuster. Kein Jargon,',
        '  keine erfundene Zahl. Eine Zeile ohne Begründung wird verworfen.',
        '- Bezeichnungen sind kurz und deutsch („Hochsaison Sommer", „Weihnachten").',
      ].join('\n')
    : [
        'You advise small landlords on their price list.',
        '',
        'DO NOT INVENT MARKET DATA. You have no access to other properties’ prices,',
        'booking portals or statistics. NEVER state a concrete comparison figure',
        '(“comparable places charge €140”, “12% below the local average”). Such numbers',
        'would be made up. Your basis is general knowledge about the region, the',
        'seasons and this type of property — say so in "basis" in one sentence, and',
        'call the result a starting point rather than a market price.',
        '',
        'Answer with THIS JSON object ONLY, no text before or after, no code fence:',
        SCHEMA,
        '',
        'Rules:',
        `- All prices are whole amounts in ${currency}, per night, for the whole unit,`,
        '  as the guest sees them (including the usual tax).',
        '- "to" is the last day, INCLUSIVE.',
        `- At most ${MAX_PLAN_PERIODS} periods. They must NOT overlap, must last between`,
        `  ${MIN_PERIOD_DAYS} and ${MAX_PERIOD_DAYS} days and must not lie in the past.`,
        `- Season prices stay between ${MIN_SEASON_FACTOR}× and ${MAX_SEASON_FACTOR}× "basePrice".`,
        `- "minNights" between ${MIN_MIN_NIGHTS} and ${MAX_MIN_NIGHTS}, or leave it out.`,
        `- "weekendPct" at most ${MAX_WEEKEND_PCT}; leave it out if no surcharge fits.`,
        '- EVERY number needs a "reason": one sentence in a landlord’s language saying',
        '  WHY — season, holidays, occasion, demand pattern. No jargon, no invented',
        '  figures. A row without a reason is discarded.',
        '- Names are short (“High season summer”, “Christmas”).',
      ].join('\n')
}

/** Was der Vermieter beim Kaltstart über sein Haus sagt. */
export interface ColdStartAnswers {
  /** Ort und Region, so wie er sie nennen würde. */
  place: string
  /** Wann bei ihm etwas los ist — Sommer, Winter, ganzjährig, Stadt. */
  character: string
  /** Wer bei ihm wohnt: Familien, Paare, Wanderer, Monteure. */
  audience: string
  /** Alles, was sonst noch den Preis erklärt. Darf leer bleiben. */
  note: string
}

/** Eine Einheit in Worten — Art, Größe, Betten, Ausstattung. */
function unitLine(u: Unit, de: boolean): string {
  const kind: Record<Unit['kind'], [string, string]> = {
    room: ['Zimmer', 'room'],
    apartment: ['Ferienwohnung', 'holiday flat'],
    house: ['Ferienhaus', 'holiday house'],
    pitch: ['Stellplatz', 'pitch'],
    other: ['Einheit', 'unit'],
  }
  const parts = [`${u.name}: ${kind[u.kind][de ? 0 : 1]}`]
  if (u.size_sqm) parts.push(`${u.size_sqm} m²`)
  if (u.bedrooms) parts.push(de ? `${u.bedrooms} Schlafzimmer` : `${u.bedrooms} bedrooms`)
  parts.push(de ? `bis ${u.max_occupancy} Personen` : `up to ${u.max_occupancy} guests`)
  if (u.amenities.length > 0) parts.push(u.amenities.slice(0, 8).join(', '))
  return `- ${parts.join(' · ')}`
}

/**
 * Der Kaltstart: keine Belegung, keine Historie, nur das Haus und ein paar Angaben.
 *
 * Die vier Fragen sind mit Bedacht wenige. Ein neuer Vermieter, der vor einer leeren
 * Preisliste steht, füllt kein Formular mit zwölf Feldern aus — er bricht ab und
 * schreibt weiter irgendeine Zahl in die Zeile. Alles, was aus den Einheiten schon
 * bekannt ist (Art, Größe, Betten, Ausstattung), wird deshalb nicht gefragt,
 * sondern mitgeschickt.
 */
export function coldStartPrompt(input: {
  answers: ColdStartAnswers
  units: Unit[]
  country: string
  today: ISODate
  de: boolean
  /** Der heute hinterlegte Preis — als Anhaltspunkt benannt, nicht als Vorgabe. */
  currentBasePrice: number | null
}): string {
  const { de, answers } = input
  const active = input.units.filter((u) => u.active)
  const lines: string[] = []

  lines.push(de
    ? 'Ein Vermieter richtet seine Preisliste zum ersten Mal ein. Es gibt noch keine'
      + ' Buchungshistorie und keine Saisonpreise.'
    : 'A landlord is setting up their price list for the first time. There is no'
      + ' booking history and there are no seasonal rates yet.')
  lines.push('')
  lines.push(de ? `Heute ist der ${input.today}.` : `Today is ${input.today}.`)
  lines.push(de ? `Land: ${input.country}` : `Country: ${input.country}`)
  lines.push(de ? `Ort/Region: ${answers.place}` : `Place/region: ${answers.place}`)
  lines.push(de ? `Saison vor Ort: ${answers.character}` : `Local season: ${answers.character}`)
  lines.push(de ? `Typische Gäste: ${answers.audience}` : `Typical guests: ${answers.audience}`)
  if (answers.note.trim()) {
    lines.push(de ? `Ergänzung: ${answers.note.trim()}` : `Additional notes: ${answers.note.trim()}`)
  }
  if (input.currentBasePrice != null) {
    lines.push(de
      ? `Aktuell hinterlegter Preis: ${input.currentBasePrice} pro Nacht (Platzhalter aus der`
        + ' Einrichtung — nur ein Anhaltspunkt, keine Vorgabe).'
      : `Currently stored price: ${input.currentBasePrice} per night (a placeholder from`
        + ' setup — an indication only, not a target).')
  }

  lines.push('')
  lines.push(de ? `Einheiten (${active.length}):` : `Units (${active.length}):`)
  for (const u of active.slice(0, 25)) lines.push(unitLine(u, de))

  lines.push('')
  lines.push(de
    ? 'Schlage einen Grundpreis, die passenden Saisonzeiträume der nächsten zwölf'
      + ' Monate mit Zu- und Abschlägen, einen Wochenendaufschlag und sinnvolle'
      + ' Mindestaufenthalte vor. Orientiere dich an den Schulferien und den'
      + ' Jahreszeiten der genannten Region.'
    : 'Propose a base price, the fitting seasonal periods for the next twelve months'
      + ' with their premiums and discounts, a weekend surcharge and sensible minimum'
      + ' stays. Take the school holidays and the seasons of that region into account.')

  return lines.join('\n')
}

/** Was ein Zeitraum aus der eigenen Belegung dem Modell zu sagen hat. */
export interface OccupancyBrief {
  from: ISODate
  lastDay: ISODate
  /** 0 … 1. */
  occupancy: number
  /** Was der Zeitraum heute im Schnitt kostet. */
  currentPrice: number
  /** Ferienregionen, die den Zeitraum überwiegend abdecken. */
  holidayRegions: string[]
  /** Ob für den Zeitraum bereits eine eigene Saison hinterlegt ist. */
  hasSeason: boolean
}

/**
 * Der zweite Fall: der Betrieb hat Zahlen, und die KI soll sie begründen.
 *
 * Die Kennzahlen kommen fertig aus `buildPriceAdvice` — das Modell rechnet hier
 * NICHTS. Es ordnet ein, formuliert und schlägt die Zeitraumgrenzen vor, an denen
 * ein Kalenderwochenraster scheitert („die Sommerferien", nicht „KW 30 bis 32").
 * Genau dafür ist es da; die Auslastung selbst zu schätzen wäre der Anfang einer
 * zweiten, schlechteren Preislogik.
 */
export function occupancyPrompt(input: {
  spans: OccupancyBrief[]
  /** Mittlerer Nachtpreis über den ganzen Betrachtungszeitraum. */
  averagePrice: number
  /** Fr/Sa gegen Mo–Do im Rückblick, als Anteile. Null, wenn es zu wenig Belege gab. */
  weekend: { weekendOccupancy: number; weekdayOccupancy: number } | null
  today: ISODate
  de: boolean
}): string {
  const { de } = input
  const pct = (v: number) => `${Math.round(v * 100)} %`
  const lines: string[] = []

  lines.push(de
    ? 'Ein Vermieter hat bereits Buchungen. Unten stehen die AUS SEINEN EIGENEN DATEN'
      + ' berechneten Kennzahlen der nächsten Monate. Rechne nichts nach und erfinde'
      + ' keine weiteren Zahlen — ordne diese ein.'
    : 'A landlord already has bookings. Below are the figures computed FROM THEIR OWN'
      + ' DATA for the coming months. Do not recalculate anything and do not invent'
      + ' further numbers — interpret these.')
  lines.push('')
  lines.push(de ? `Heute ist der ${input.today}.` : `Today is ${input.today}.`)
  lines.push(de
    ? `Mittlerer Nachtpreis über den Zeitraum: ${Math.round(input.averagePrice)}`
    : `Average nightly price over the window: ${Math.round(input.averagePrice)}`)

  lines.push('')
  lines.push(de ? 'Zeiträume:' : 'Periods:')
  for (const s of input.spans) {
    const bits = [
      `${s.from} … ${s.lastDay}`,
      de ? `Auslastung ${pct(s.occupancy)}` : `occupancy ${pct(s.occupancy)}`,
      de ? `heutiger Preis ${Math.round(s.currentPrice)}` : `price today ${Math.round(s.currentPrice)}`,
      s.hasSeason
        ? (de ? 'eigene Saison hinterlegt' : 'has its own season')
        : (de ? 'keine Saison, es greift der Grundpreis' : 'no season, base price applies'),
    ]
    if (s.holidayRegions.length > 0) {
      bits.push(de
        ? `Schulferien in ${s.holidayRegions.join(', ')}`
        : `school holidays in ${s.holidayRegions.join(', ')}`)
    }
    lines.push(`- ${bits.join(' · ')}`)
  }

  if (input.weekend) {
    lines.push('')
    lines.push(de
      ? `Rückblick: Fr/Sa zu ${pct(input.weekend.weekendOccupancy)} belegt, Mo–Do zu`
        + ` ${pct(input.weekend.weekdayOccupancy)}.`
      : `Looking back: Fri/Sat ran at ${pct(input.weekend.weekendOccupancy)}, Mon–Thu at`
        + ` ${pct(input.weekend.weekdayOccupancy)}.`)
  }

  lines.push('')
  lines.push(de
    ? 'Sag in den Begründungen ausdrücklich, welche Zeiträume gefragt sind und wo noch'
      + ' Luft ist, und beziehe dich dabei auf die Zahlen oben. Fasse benachbarte'
      + ' Wochen zu Zeiträumen zusammen, die ein Vermieter als EINE Saison denkt.'
    : 'In your reasons, say explicitly which periods are in demand and where there is'
      + ' still room, referring to the figures above. Merge neighbouring weeks into'
      + ' periods a landlord would think of as ONE season.')

  return lines.join('\n')
}
